import { addMemberSchema, type Member, type TenantRole } from '@agents/shared';
import { useState, type FormEvent } from 'react';
import { useCan, useCurrentMe } from '../components/shell';
import {
  Button,
  ConfirmDialog,
  ErrorState,
  Field,
  FormDialog,
  OverflowMenu,
  PageHeader,
  PersonAvatar,
  problemMessage,
  TableSkeleton,
  useToast,
} from '../components/ui';
import { ApiProblem } from '../lib/api';
import { useDocumentTitle } from '../lib/document-title';
import { clearError } from '../lib/forms';
import { dateTime, relativeTime } from '../lib/format';
import { useAddMember, useMembers, useRemoveMember } from '../lib/queries';

const ROLE_LABEL: Record<TenantRole, string> = { tenant_admin: 'Administrador', tenant_member: 'Miembro' };

const ROLE_OPTIONS: { value: TenantRole; text: string }[] = [
  { value: 'tenant_member', text: 'Ejecuta agentes publicados y revisa sus resultados.' },
  { value: 'tenant_admin', text: 'Además crea, edita y publica agentes, y gestiona el equipo.' },
];

function AddMemberDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const add = useAddMember();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TenantRole>('tenant_member');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const close = (next: boolean) => {
    if (!next) {
      setEmail('');
      setRole('tenant_member');
      setErrors({});
    }
    onOpenChange(next);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = addMemberSchema.safeParse({ email, role });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [i.path.join('.'), i.message])));
      return;
    }
    setErrors({});
    try {
      await add.mutateAsync(parsed.data);
      toast(`${parsed.data.email} ahora es ${ROLE_LABEL[parsed.data.role].toLowerCase()}`);
      close(false);
    } catch (err) {
      if (err instanceof ApiProblem) setErrors({ email: err.problem.detail ?? err.problem.title });
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={close}
      title="Agregar miembro"
      description="Podrá entrar cuando inicie sesión con este correo en Google o Microsoft."
      submitLabel="Agregar miembro"
      onSubmit={submit}
      loading={add.isPending}
    >
      <Field label="Correo corporativo" error={errors.email}>
        {(p) => (
          <input
            {...p}
            className="input"
            type="email"
            autoComplete="off"
            placeholder="nombre@empresa.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors(clearError('email'));
            }}
          />
        )}
      </Field>
      <fieldset className="choice-group">
        <legend>Rol</legend>
        {ROLE_OPTIONS.map((o) => (
          <label key={o.value} className="choice">
            <input
              type="radio"
              name="role"
              value={o.value}
              checked={role === o.value}
              onChange={() => setRole(o.value)}
            />
            <span>
              <strong>{ROLE_LABEL[o.value]}</strong>
              <small>{o.text}</small>
            </span>
            <span />
          </label>
        ))}
      </fieldset>
    </FormDialog>
  );
}

export function TeamPage() {
  useDocumentTitle('Equipo');
  const me = useCurrentMe();
  const can = useCan();
  const members = useMembers();
  const remove = useRemoveMember();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [target, setTarget] = useState<Member | null>(null);
  const manage = can('member.manage');

  const doRemove = async () => {
    if (!target) return;
    try {
      await remove.mutateAsync(target.userId);
      toast(`${target.email} ya no tiene acceso`);
    } catch (err) {
      toast('No se pudo quitar el acceso', {
        tone: 'danger',
        detail: problemMessage(err, 'Inténtalo de nuevo.'),
      });
    } finally {
      setTarget(null);
    }
  };

  const spaceName = me.activeTenant?.name ?? 'este espacio';

  return (
    <>
      <PageHeader
        title="Equipo"
        description={
          manage
            ? `Personas con acceso a ${spaceName}. Los administradores gestionan agentes y equipo; los miembros ejecutan agentes.`
            : `Personas con acceso a ${spaceName}.`
        }
        actions={
          manage && (
            <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
              Agregar miembro
            </Button>
          )
        }
      />
      {members.isPending && <TableSkeleton rows={4} cols={3} />}
      {members.isError && <ErrorState error={members.error} onRetry={() => void members.refetch()} />}
      {members.data && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Persona</th>
                <th scope="col">Rol</th>
                <th scope="col" data-optional>
                  Desde
                </th>
                {manage && (
                  <th scope="col">
                    <span className="visually-hidden">Acciones</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {members.data.map((m) => {
                const self = m.userId === me.id;
                const name = m.displayName ?? m.email;
                return (
                  <tr key={m.userId}>
                    <td>
                      <div className="cell-main">
                        <PersonAvatar name={name} />
                        <div>
                          <span className="name">{name}</span>
                          {self && (
                            <span className="chip" style={{ marginLeft: 6 }}>
                              Tú
                            </span>
                          )}
                          {m.displayName && <small>{m.email}</small>}
                        </div>
                      </div>
                    </td>
                    <td>{ROLE_LABEL[m.role]}</td>
                    <td className="muted nowrap" data-optional title={dateTime(m.createdAt)}>
                      {relativeTime(m.createdAt)}
                    </td>
                    {manage && (
                      <td className="actions-cell">
                        {!self && (
                          <OverflowMenu
                            size="sm"
                            label={`Acciones para ${name}`}
                            actions={[
                              {
                                label: 'Quitar acceso',
                                icon: 'x',
                                danger: true,
                                onSelect: () => setTarget(m),
                              },
                            ]}
                          />
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <AddMemberDialog open={adding} onOpenChange={setAdding} />
      <ConfirmDialog
        open={!!target}
        onOpenChange={(o) => !o && setTarget(null)}
        title={`¿Quitar a ${target?.displayName ?? target?.email ?? ''}?`}
        description={`Perderá acceso inmediato a los agentes y ejecuciones de ${spaceName}. Puedes volver a agregarlo después.`}
        confirmLabel="Quitar acceso"
        onConfirm={() => void doRemove()}
        loading={remove.isPending}
      />
    </>
  );
}
